import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../components/layout/AppLayout';
import StatusBadge from '../../components/ui/StatusBadge';
import { getStats, getQuickStats, getRecentActivity } from '../../api/dashboard';
import { getServiceAlerts } from '../../api/serviceSchedules';
import { getPayments } from '../../api/payments';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [quickStats, setQuickStats] = useState(null);
  const [recentContracts, setRecentContracts] = useState([]);
  const [serviceAlerts, setServiceAlerts] = useState([]);
  const [paymentAlerts, setPaymentAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAlert, setShowAlert] = useState(true);
  const [showServiceAlert, setShowServiceAlert] = useState(true);
  const [showPaymentAlert, setShowPaymentAlert] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
      const today = new Date();
      const in5Days = new Date(today); in5Days.setDate(today.getDate() + 5);
      const todayStr = today.toISOString().slice(0, 10);
      const in5DaysStr = in5Days.toISOString().slice(0, 10);

      const [statsRes, quickRes, recentRes, alertsRes, paymentsRes] = await Promise.allSettled([
        getStats(),
        getQuickStats(),
        getRecentActivity(),
        getServiceAlerts(),
        getPayments({ status: 'pending', from_date: todayStr, to_date: in5DaysStr }),
      ]);

      const val = (r) => r.status === 'fulfilled' ? r.value : null;

      if (val(statsRes))   setStats(val(statsRes));
      if (val(quickRes))   setQuickStats(val(quickRes));
      if (val(recentRes))  setRecentContracts(val(recentRes)?.recent_activity || val(recentRes)?.data?.data || val(recentRes)?.data || []);
      if (val(alertsRes))  setServiceAlerts(val(alertsRes)?.data || val(alertsRes) || []);
      if (val(paymentsRes)) {
        const dueSoon = (val(paymentsRes)?.data || []).filter(p =>
          p.due_date && p.due_date >= todayStr && p.due_date <= in5DaysStr
        );
        setPaymentAlerts(dueSoon);
      }

      } catch (err) {
        console.error('Dashboard fetch error', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <AppLayout title="Dashboard" breadcrumbs="Dashboard">
        <div className="text-center py-12 text-gray-500">Loading dashboard...</div>
      </AppLayout>
    );
  }

  const expiringSoonCount = stats?.expiring_soon_contracts || 0;

  return (
    <AppLayout title="Dashboard" breadcrumbs="Dashboard">
      <div className="space-y-6">

        {/* Alert Banner */}
        {showAlert && expiringSoonCount > 0 && (
          <div
            className="flex items-start gap-4 p-5 rounded-xl relative border-l-4 border-l-yellow-500"
            style={{
              background: 'linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(245,158,11,0.02) 100%)',
              boxShadow: '0 4px 6px rgba(0,0,0,0.06)',
            }}
          >
            <span className="text-[32px] leading-none flex-shrink-0">⚠️</span>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900 mb-1.5">Action Required: Contracts Expiring Soon</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                <strong className="text-gray-900 font-semibold">{expiringSoonCount} contracts</strong> are expiring in the next 30 days. Review and take action to avoid service disruption.
              </p>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => navigate('/contracts')}
                  className="px-5 py-2 text-sm font-medium rounded-lg text-white transition-all hover:opacity-90"
                  style={{ background: '#043959' }}
                >
                  View All Contracts
                </button>
                <button
                  onClick={() => navigate('/contracts?status=expiring_soon')}
                  className="px-5 py-2 text-sm font-medium rounded-lg bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-all"
                >
                  View Expiring Only
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowAlert(false)}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1 rounded hover:bg-black/5 transition-colors"
            >
              &times;
            </button>
          </div>
        )}

        {/* Service Maintenance Alert */}
        {showServiceAlert && serviceAlerts.length > 0 && (
          <div
            className="flex items-start gap-4 p-5 rounded-xl relative border-l-4"
            style={{
              borderLeftColor: serviceAlerts.some((a) => a.days_until_due <= 7) ? '#EF4444' : serviceAlerts.some((a) => a.days_until_due <= 15) ? '#F97316' : '#EAB308',
              background: serviceAlerts.some((a) => a.days_until_due <= 7)
                ? 'linear-gradient(135deg, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0.02) 100%)'
                : serviceAlerts.some((a) => a.days_until_due <= 15)
                  ? 'linear-gradient(135deg, rgba(249,115,22,0.08) 0%, rgba(249,115,22,0.02) 100%)'
                  : 'linear-gradient(135deg, rgba(234,179,8,0.08) 0%, rgba(234,179,8,0.02) 100%)',
              boxShadow: '0 4px 6px rgba(0,0,0,0.06)',
            }}
          >
            <span className="text-[32px] leading-none flex-shrink-0">🔧</span>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900 mb-1.5">Service Maintenance Due</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                <strong className="text-gray-900 font-semibold">{serviceAlerts.length} service {serviceAlerts.length === 1 ? 'item' : 'items'}</strong> due within 30 days:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
                {serviceAlerts.map((alert) => (
                  <p key={alert.id} className="text-sm" style={{ margin: 0, color: alert.days_until_due <= 7 ? '#DC2626' : alert.days_until_due <= 15 ? '#EA580C' : '#CA8A04' }}>
                    {alert.service_name} — {alert.is_overdue ? 'Overdue!' : `${alert.days_until_due} days remaining`}
                  </p>
                ))}
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => navigate('/water-treatment')}
                  className="px-5 py-2 text-sm font-medium rounded-lg text-white transition-all hover:opacity-90"
                  style={{ background: '#043959' }}
                >
                  View Water Treatment
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowServiceAlert(false)}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1 rounded hover:bg-black/5 transition-colors"
            >
              &times;
            </button>
          </div>
        )}

        {/* Payment Due Alert */}
        {showPaymentAlert && paymentAlerts.length > 0 && (
          <div
            className="flex items-start gap-4 p-5 rounded-xl relative border-l-4"
            style={{
              borderLeftColor: '#3B82F6',
              background: 'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(59,130,246,0.02) 100%)',
              boxShadow: '0 4px 6px rgba(0,0,0,0.06)',
            }}
          >
            <span className="text-[32px] leading-none flex-shrink-0">💳</span>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900 mb-1.5">Payments Due Soon</h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-2">
                <strong className="text-gray-900 font-semibold">{paymentAlerts.length} payment{paymentAlerts.length > 1 ? 's' : ''}</strong> due within the next 5 days:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {paymentAlerts.map(p => {
                  const due = new Date(p.due_date);
                  const today = new Date(); today.setHours(0,0,0,0);
                  const daysLeft = Math.round((due - today) / 86400000);
                  return (
                    <p key={p.id} className="text-sm" style={{ margin: 0, color: daysLeft <= 1 ? '#DC2626' : daysLeft <= 3 ? '#EA580C' : '#2563EB' }}>
                      {p.customer_name} — ₹{Number(p.amount).toLocaleString('en-IN')} {daysLeft === 0 ? '(due today)' : `(${daysLeft}d left)`}
                    </p>
                  );
                })}
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => navigate('/payments')}
                  className="px-5 py-2 text-sm font-medium rounded-lg text-white transition-all hover:opacity-90"
                  style={{ background: '#043959' }}
                >
                  View Payments
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowPaymentAlert(false)}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1 rounded hover:bg-black/5 transition-colors"
            >
              &times;
            </button>
          </div>
        )}

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            {
              value: stats?.active_contracts || 0,
              label: 'Active Contracts',
              trend: `${stats?.total_contracts || 0} total contracts`,
              trendColor: 'text-green-600',
              emoji: '📋',
              iconBg: 'linear-gradient(135deg, rgba(4,57,89,0.1), rgba(4,57,89,0.2))',
              onClick: () => navigate('/contracts'),
            },
            {
              value: expiringSoonCount,
              label: 'Expiring Soon',
              trend: 'Next 30 days',
              trendColor: expiringSoonCount > 0 ? 'text-red-500' : 'text-green-600',
              emoji: '⏰',
              iconBg: 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(245,158,11,0.2))',
              onClick: () => navigate('/contracts'),
            },
            {
              value: stats?.active_services || 0,
              label: 'Active Services',
              trend: 'Scheduled maintenance',
              trendColor: 'text-green-600',
              emoji: '🔧',
              iconBg: 'linear-gradient(135deg, rgba(16,185,129,0.1), rgba(16,185,129,0.2))',
              onClick: () => navigate('/water-treatment'),
            },
            {
              value: stats?.pending_quotations || 0,
              label: 'Pending Quotations',
              trend: 'Awaiting review',
              trendColor: stats?.pending_quotations > 0 ? 'text-amber-500' : 'text-gray-400',
              emoji: '📄',
              iconBg: 'linear-gradient(135deg, rgba(139,92,246,0.1), rgba(139,92,246,0.2))',
              onClick: () => navigate('/quotations'),
            },
          ].map((card, i) => (
            <div
              key={i}
              onClick={card.onClick}
              className="bg-white rounded-2xl p-6 relative overflow-hidden transition-all duration-300 hover:-translate-y-1 group cursor-pointer"
              style={{ border: '1px solid #E5E5E0', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.08)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.04)'}
            >
              {/* Top gradient bar on hover */}
              <div className="absolute top-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: 'linear-gradient(90deg, #043959, #F4A460)' }} />

              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-[32px] font-bold text-gray-900 leading-none">{card.value}</p>
                  <p className="text-[13px] text-gray-500 mt-1">{card.label}</p>
                </div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl" style={{ background: card.iconBg }}>
                  {card.emoji}
                </div>
              </div>

              {/* Trend with separator */}
              <div className="pt-3 mt-3" style={{ borderTop: '1px solid #F0F0EB' }}>
                <p className={`text-xs ${card.trendColor}`}>{card.trend}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { label: 'New Contract', sub: 'Create MOU', emoji: '📝', onClick: () => navigate('/contracts') },
            { label: 'Upload Quotation', sub: 'Compare vendors', emoji: '📤', onClick: () => navigate('/quotations') },
            { label: 'Assign Staff', sub: 'Manage allocation', emoji: '👤', onClick: () => navigate('/staff') },
            { label: 'View Calendar', sub: 'Track contracts', emoji: '📅', onClick: () => navigate('/calendar') },
          ].map((action, i) => (
            <button
              key={i}
              onClick={action.onClick}
              className="flex items-center gap-3 p-4 bg-white rounded-xl text-left transition-all duration-200 hover:-translate-y-0.5"
              style={{ border: '1px solid #E5E5E0' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#043959'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.06)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E5E0'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div className="w-12 h-12 rounded-[10px] flex items-center justify-center text-2xl"
                style={{ background: 'linear-gradient(135deg, #043959, #055a87)' }}>
                <span className="grayscale-0 brightness-200 contrast-100">{action.emoji}</span>
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-[15px]">{action.label}</p>
                <p className="text-xs text-gray-400 mt-0.5">{action.sub}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Recent Contracts Table */}
        <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid #E5E5E0' }}>
          <div className="flex items-center justify-between px-6 py-5">
            <h2 className="text-lg font-semibold text-gray-900">Recent Contracts</h2>
            <button
              onClick={() => navigate('/contracts')}
              className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-white rounded-lg hover:bg-gray-50 transition-colors"
              style={{ border: '1px solid #E5E5E0' }}
            >
              View All
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase" style={{ letterSpacing: '0.5px' }}>Service Type</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase" style={{ letterSpacing: '0.5px' }}>Vendor</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase" style={{ letterSpacing: '0.5px' }}>Start Date</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase" style={{ letterSpacing: '0.5px' }}>End Date</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase" style={{ letterSpacing: '0.5px' }}>Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase" style={{ letterSpacing: '0.5px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentContracts.slice(0, 5).map((contract) => (
                  <tr
                    key={contract.id}
                    className="transition-colors hover:bg-[#FAFAF8]"
                    style={{ borderBottom: '1px solid #F0F0EB' }}
                  >
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                      {contract.custom_service_name || contract.service_category?.name || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{contract.vendor?.name || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{formatDate(contract.start_date)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{formatDate(contract.end_date)}</td>
                    <td className="px-6 py-4"><StatusBadge status={contract.status} /></td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => navigate('/contracts')}
                        className="px-3 py-1 text-xs font-medium text-gray-600 bg-white rounded-md hover:bg-gray-50 transition-colors"
                        style={{ border: '1px solid #E5E5E0' }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {recentContracts.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-400">No contracts found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </AppLayout>
  );
}
